import { parseOwnershipPercentage } from './editOwnershipPercentage';

// Keep Mermaid's existing paragraph, typography and background. Only the
// numeric text becomes editable; the percentage sign remains in place.
export function bindInlineOwnershipPercentage(label, { name, getMaximum = () => 100, onChange, onError, onFinish = () => {} }) {
  const paragraph = label.querySelector('foreignObject p');
  if (!paragraph) return;
  let saved = paragraph.textContent.trim().replace(/%$/, '').trim();
  const input = document.createElement('span');
  input.className = 'ownershipPercentageInput';
  input.contentEditable = 'true';
  input.setAttribute('role', 'textbox');
  input.setAttribute('tabindex', '0');
  input.setAttribute('inputmode', 'decimal');
  input.setAttribute('aria-label', `Ownership percentage for ${name || 'this owner'}`);
  input.setAttribute('aria-multiline', 'false');
  input.setAttribute('spellcheck', 'false');
  input.textContent = saved;
  paragraph.replaceChildren(input, document.createTextNode('%'));
  label.classList.add('ownershipPercentageEditable');
  label.style.pointerEvents = 'all';
  label.style.cursor = 'text';
  // Allow a longer value without clipping it or shifting the edge midpoint.
  label.querySelector('foreignObject').style.overflow = 'visible';
  const body = label.querySelector('foreignObject > div');
  if (body) {
    Object.assign(body.style, {
      display: 'block', width: 'max-content', maxWidth: 'none',
      position: 'relative', left: '50%', transform: 'translateX(-50%)',
    });
  }
  const selectNumber = () => {
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(input);
    selection?.removeAllRanges();
    selection?.addRange(range);
  };
  let draft = saved;
  const validDraft = (text) => /^\d*\.?\d*$/.test(text);
  const placeCaretAtEnd = () => {
    const range = document.createRange();
    range.selectNodeContents(input);
    range.collapse(false);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  };
  const constrainDraft = () => {
    let text = input.textContent;
    if (!validDraft(text)) text = draft;
    if (text !== '' && text !== '.' && Number(text) > getMaximum()) {
      text = String(getMaximum());
    }
    if (input.textContent !== text) {
      input.textContent = text;
      placeCaretAtEnd();
    }
    draft = text;
  };
  input.addEventListener('beforeinput', (event) => {
    if (event.data && !/^[\d.]+$/.test(event.data)) event.preventDefault();
  });
  input.addEventListener('input', constrainDraft);
  const commit = (leaving = false) => {
    if (input.textContent === saved) {
      input.removeAttribute('aria-invalid');
      onError('');
      return true;
    }
    const parsed = parseOwnershipPercentage(input.textContent);
    const value = parsed === null ? null : Math.min(parsed, getMaximum());
    if (value === null) {
      onError('Enter a percentage between 0 and 100. The ownership percentage has not been changed.');
      input.setAttribute('aria-invalid', 'true');
      if (leaving) { input.textContent = saved; draft = saved; }
      return false;
    }
    try {
      if (saved === '' || value !== Number(saved)) onChange(value);
      saved = String(value);
      draft = saved;
      input.textContent = saved;
      input.removeAttribute('aria-invalid');
      onError('');
      return true;
    } catch (error) {
      onError(error.message);
      if (leaving) { input.textContent = saved; draft = saved; }
      return false;
    }
  };
  input.addEventListener('focus', selectNumber);
  // Prevent the initial mouse-up from collapsing the selected number.
  input.addEventListener('mousedown', (event) => {
    if (document.activeElement !== input) {
      event.preventDefault();
      input.focus();
    }
  });
  label.addEventListener('click', () => {
    if (document.activeElement !== input) input.focus();
  });
  input.addEventListener('blur', () => { commit(true); onFinish(saved); });
  input.addEventListener('keydown', (event) => {
    event.stopPropagation();
    if (event.key === 'Enter') {
      event.preventDefault();
      if (commit()) input.blur();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      input.textContent = saved;
      draft = saved;
      onError('');
      input.blur();
    } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !/^[\d.]$/.test(event.key)) {
      event.preventDefault();
    }
  });
  input.addEventListener('paste', (event) => {
    event.preventDefault();
    const text = event.clipboardData?.getData('text/plain') || '';
    if (!validDraft(text)) return;
    input.textContent = text;
    constrainDraft();
    selectNumber();
  });
  input.addEventListener('drop', (event) => event.preventDefault());
  return input;
}


// Give a thin arrow a generous invisible click target, then place the editor
// at its midpoint in the same SVG coordinate system as the path.
export function bindMissingOwnershipPercentage(path, options, background) {
  const svgNS = 'http://www.w3.org/2000/svg';
  const hit = path.cloneNode(false);
  hit.removeAttribute('id');
  hit.removeAttribute('marker-end');
  hit.removeAttribute('marker-start');
  hit.setAttribute('class', 'ownershipPercentageArrowTarget');
  hit.setAttribute('role', 'button');
  hit.setAttribute('tabindex', '0');
  hit.setAttribute('aria-label', `Add ownership percentage for ${options.name || 'this owner'}`);
  Object.assign(hit.style, {
    stroke: 'transparent', strokeWidth: '16px', fill: 'none',
    pointerEvents: 'stroke', cursor: 'text', outline: 'none',
    boxShadow: 'none',
  });
  hit.style.setProperty('-webkit-tap-highlight-color', 'transparent');
  // Do not briefly focus the entire SVG path before moving focus to the input.
  hit.addEventListener('mousedown', (event) => event.preventDefault());
  path.parentNode.appendChild(hit);
  let editor;
  let input;
  const open = () => {
    if (!editor) {
      const midpoint = path.getPointAtLength(path.getTotalLength() / 2);
      editor = document.createElementNS(svgNS, 'g');
      editor.setAttribute('class', 'edgeLabel ownershipPercentageNewLabel');
      editor.setAttribute('transform', `translate(${midpoint.x - 30},${midpoint.y - 12})`);
      const foreign = document.createElementNS(svgNS, 'foreignObject');
      foreign.setAttribute('width', '60');
      foreign.setAttribute('height', '24');
      const body = document.createElementNS('http://www.w3.org/1999/xhtml', 'div');
      const paragraph = document.createElementNS('http://www.w3.org/1999/xhtml', 'p');
      Object.assign(paragraph.style, { margin: '0', background: background || '#FFFCF9', padding: '0 3px', whiteSpace: 'nowrap' });
      body.appendChild(paragraph);
      foreign.appendChild(body);
      editor.appendChild(foreign);
      path.parentNode.appendChild(editor);
      input = bindInlineOwnershipPercentage(editor, {
        ...options,
        onFinish: (value) => { if (value === '') editor.style.display = 'none'; },
      });
    }
    editor.style.display = '';
    input.focus();
  };
  hit.addEventListener('click', open);
  hit.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      open();
    }
  });
}
